import { Image } from '@educator/components/Image'
import type { AiLabsCardItem } from './types'

// Fallback if a card lacks a bgColor.
const FALLBACK_BG_COLOR = '#76909a'

export interface AiLabsCardProps extends AiLabsCardItem {
  /** Peeking neighbours dim. */
  isActive: boolean
  /** A looping carousel's cloned copy — hidden from assistive tech. */
  isDuplicate?: boolean
}
export const AiLabsCard = ({
  title,
  description,
  bgColor,
  desktopImageUrl,
  mobileImageUrl,
  isActive,
  isDuplicate = false,
}: AiLabsCardProps) => {
  const hasArt = Boolean(desktopImageUrl || mobileImageUrl)

  return (
    <article
      aria-label={isDuplicate ? undefined : title}
      aria-hidden={isDuplicate || undefined}
      className={`edu-surface-dark relative h-[480px] w-[288px] shrink-0 snap-start overflow-hidden border border-edu-stroke-subtle transition-opacity duration-500 ease-in-out md:w-full md:max-w-[960px] ${
        isActive ? 'opacity-100' : 'opacity-20'
      }`}
      // Base layer; art covers it on desktop, stands in on mobile.
      style={{ backgroundColor: bgColor || FALLBACK_BG_COLOR }}
    >
      {hasArt && (
        <div className='absolute inset-0' aria-hidden='true'>
          {mobileImageUrl && (
            <div className='absolute inset-0 md:hidden'>
              <Image
                src={mobileImageUrl}
                alt=''
                fill
                className='object-cover'
                sizes='288px'
              />
            </div>
          )}
          {desktopImageUrl && (
            <div className='absolute inset-0 hidden md:block'>
              <Image
                src={desktopImageUrl}
                alt=''
                fill
                className='object-cover object-left'
                sizes='960px'
              />
            </div>
          )}
        </div>
      )}

      {/* Scrim for title legibility. */}
      <div
        aria-hidden='true'
        className='absolute inset-x-0 bottom-0 z-10 h-1/2 bg-gradient-to-b from-transparent to-black/75 min-[1160px]:hidden'
      />

      {/* Bottom-left until the card reaches its full 960px; a right-half overlay from there. */}
      <div className='absolute inset-x-0 bottom-0 z-20 flex max-w-[393px] flex-col gap-3 px-4 py-6 text-edu-fg-primary min-[1160px]:inset-x-auto min-[1160px]:bottom-auto min-[1160px]:right-12 min-[1160px]:top-[250px] min-[1160px]:w-[393px] min-[1160px]:p-0'>
        {/* Mobile is Ivar Headline 500 at 36px per Figma — between serif-3 (32)
            and serif-2 (48), so no Boogie token matches. */}
        <h3 className='m-0 font-serif text-[36px] font-medium leading-none md:text-title-serif-2'>
          {title}
        </h3>
        {description && (
          <p className='m-0 text-body-2 leading-body-2'>{description}</p>
        )}
      </div>
    </article>
  )
}
